import { useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { matchApi } from '@/api';
import { mergeTrackBoxesChunk } from '@/store/supertrackSlice';

const PREFETCH_THRESHOLD_SECONDS = 60; // prefetch 1 min before boundary
const CHUNK_SIZE_SECONDS = 300; // 5 minutes per chunk

/**
 * Merge a new interval [start, end] into a sorted array of non-overlapping intervals.
 * Adjacent intervals (gap = 0) are also merged.
 */
function mergeInterval(intervals, start, end) {
  const result = [];
  let merged = [start, end];
  let pushed = false;

  for (const iv of intervals) {
    if (iv[1] < merged[0]) {
      result.push(iv);
    } else if (iv[0] > merged[1]) {
      if (!pushed) { result.push(merged); pushed = true; }
      result.push(iv);
    } else {
      merged = [Math.min(merged[0], iv[0]), Math.max(merged[1], iv[1])];
    }
  }
  if (!pushed) result.push(merged);
  return result;
}

/**
 * Check whether a frame is covered by any of the loaded intervals.
 */
function isCovered(intervals, frame) {
  for (const [s, e] of intervals) {
    if (frame >= s && frame <= e) return true;
    if (s > frame) break;
  }
  return false;
}

/**
 * Check whether the entire range [start, end] is covered by loaded intervals.
 */
function isRangeCovered(intervals, start, end) {
  let cursor = start;
  for (const [s, e] of intervals) {
    if (s > cursor) return false;
    if (s <= cursor && e >= cursor) {
      cursor = e;
      if (cursor >= end) return true;
    }
  }
  return cursor >= end;
}

/**
 * Get the end of the interval covering `frame`, or -1 if not covered.
 */
function getCoveringEnd(intervals, frame) {
  for (const [s, e] of intervals) {
    if (frame >= s && frame <= e) return e;
    if (s > frame) break;
  }
  return -1;
}

export function useChunkedTrackBoxes(videoId) {
  const dispatch = useDispatch();
  const loadedIntervalsRef = useRef([]);
  const pendingRef = useRef(false);

  const loadChunk = useCallback(async (frameStart, frameEnd) => {
    if (!videoId || pendingRef.current) return;

    // Skip if fully within already-loaded intervals
    if (isRangeCovered(loadedIntervalsRef.current, frameStart, frameEnd)) return;

    pendingRef.current = true;
    const toastId = toast.loading('Loading track data…');
    try {
      const { tracks } = await matchApi.getMatchSuperTracksFull(videoId, {
        frame_start: frameStart,
        frame_end: frameEnd,
      });
      if (tracks && Object.keys(tracks).length > 0) {
        dispatch(mergeTrackBoxesChunk(tracks));
      }
      loadedIntervalsRef.current = mergeInterval(
        loadedIntervalsRef.current,
        frameStart,
        frameEnd,
      );
      toast.dismiss(toastId);
    } catch (err) {
      toast.update(toastId, { render: 'Failed to load track data', type: 'error', isLoading: false, autoClose: 3000 });
      throw err;
    } finally {
      pendingRef.current = false;
    }
  }, [videoId, dispatch]);

  const checkPrefetch = useCallback((currentFrame, fps) => {
    if (!fps) return;
    const threshold = Math.round(fps * PREFETCH_THRESHOLD_SECONDS);
    const chunkSize = Math.round(fps * CHUNK_SIZE_SECONDS);
    const coverEnd = getCoveringEnd(loadedIntervalsRef.current, currentFrame);

    if (coverEnd >= 0 && currentFrame >= coverEnd - threshold) {
      loadChunk(coverEnd, coverEnd + chunkSize);
    }
  }, [loadChunk]);

  const handleSeek = useCallback((targetFrame, fps) => {
    if (!fps) return;
    if (!isCovered(loadedIntervalsRef.current, targetFrame)) {
      const halfWindow = Math.round(fps * CHUNK_SIZE_SECONDS / 2);
      const chunkStart = Math.max(0, targetFrame - halfWindow);
      const chunkEnd = targetFrame + halfWindow;
      loadChunk(chunkStart, chunkEnd);
    }
  }, [loadChunk]);

  return {
    loadedRange: loadedIntervalsRef,
    loadChunk,
    checkPrefetch,
    handleSeek,
  };
}
